import { multiplyQtyByFactor, trimZeros } from '@/features/inventory/conversion';
import { parseSerials } from '@/features/purchases/grn-rules';

/**
 * WHERE A SERIAL IS TYPED — the client half of BR-MD-03-16/-17, shared by
 * every grid that moves stock OUT (SAL-04's invoice grid, SAL-03's delivery
 * grid, INV-08's Stock Out voucher), back IN (SAL-05's credit note, INV-08's
 * Stock In voucher), and by the one grid that only says what it SEES —
 * INV-05 §5.7's stock take, where the same block asks which units make the
 * counted quantity up.
 *
 * It lives beside the sales screens because that is where it was first
 * needed, not because it belongs to them: one rule gets one interaction
 * wherever goods cross the door.
 *
 * THE INTERACTION, AND WHY IT IS THIS ONE. The only thing the specification
 * actually draws is `OQ-05-17`, and it draws it as a ruling rather than a
 * question: *"A sub-table that expands under the kit line, one block per
 * tracked component. A dialog hides how many components still need picking.
 * The rule that they must be picked is settled by R7; only the presentation
 * is open."* Two instructions come out of that sentence and both are followed
 * here — the capture is a SUB-TABLE UNDER THE LINE, not a dialog, and it
 * SHOWS WHAT IS STILL OUTSTANDING, because being able to see that at a glance
 * is the stated reason the dialog was rejected.
 *
 * The same shape then carries a plain tracked line, and that is a decision
 * rather than an accident. `SAL-00 §5d` and `SAL-03 §5c` draw fixed column
 * sets with no tracking column in either, so a serial cell would be an
 * invention in a grid that is already eight columns wide — and two different
 * interactions for one rule on one screen is how a clerk fills in the kit's
 * serials and misses the plain line's. One block per thing that moves: the
 * item itself, or one per tracked component when a kit expands (R7 —
 * "each component behaves on a document exactly as it would if somebody had
 * typed it on a line by itself").
 *
 * WHAT EACH KIND ASKS FOR (MD-03 panel 7, field 27):
 *   - `serial` — "every single unit carries its own number", so the block
 *     wants exactly one identifier per unit and says how many are still
 *     missing. The server counts them again; this is what stops the clerk
 *     finding out at Post.
 *   - `batch` — "units are grouped into batches", so the block wants ONE lot
 *     number covering the whole quantity, and shows the quantity it covers.
 *
 * Nothing here decides whether a document may post. It cannot: the identifier
 * has to be checked against the shelf, and only the ledger knows what is on
 * it. This is where a serial is TYPED, not where it is judged.
 */

export type TrackingKind = 'serial' | 'batch';
export type TrackingType = 'none' | TrackingKind;

/** The MD-03 facts a grid needs about a picked item. Shape of `ItemResponse`. */
export interface TrackedItem {
  id: string;
  name: string;
  type: 'product' | 'service' | 'bundle';
  /** Field 7 — the one switch that decides what a kit is. */
  autoDeduct: boolean;
  trackingType: TrackingType;
  components: readonly {
    componentItemId: string;
    componentName: string;
    /** Field 8d, read from the component's own record. */
    componentTracking: TrackingType;
    /** BR-MD-03-18: the per-kit quantity in the COMPONENT's stock unit. */
    baseQty: string;
  }[];
}

/** One block of the sub-table: one thing that moves and must be identified. */
export interface CaptureBlock {
  /** null = the line's own item; otherwise the kit component (OQ-05-17). */
  componentItemId: string | null;
  name: string;
  kind: TrackingKind;
  /** How many units this block accounts for, as the document will move them. */
  units: string;
  /**
   * Serial blocks only: how many identifiers are wanted. Null when the
   * quantity is not whole units — the server refuses that with its own
   * sentence (a serial is a unit, so half a unit cannot carry one) and the
   * block must not silently ask for a rounded number instead.
   */
  needed: number | null;
}

/** The key a screen holds one block's typed text under. */
export function blockKey(block: Pick<CaptureBlock, 'componentItemId'>): string {
  return block.componentItemId ?? '·self';
}

const WHOLE = /^\d+(\.0+)?$/;
const QTY = /^\d{1,12}(\.\d{1,6})?$/;

/**
 * What a line must identify before it can post — empty for the ordinary
 * untracked line, which is most of them.
 *
 * A kit with auto-deduct OFF is a stocked thing in its own right (field 7),
 * so it is asked about ITSELF and its parts are left alone; ON, it never
 * moves as itself and the blocks are its tracked parts.
 *
 * `expandKits: false` is for the screens whose SERVER does not expand a kit
 * — a warehouse transfer moves the line's own item, and so does a hand-
 * written adjustment. Asking there for a component's serial would collect
 * identifiers nothing will ever use, and asking twice for one rule is how a
 * clerk fills in one box and misses the other. The screen asks for exactly
 * what its document will move.
 */
export function captureBlocks(
  item: TrackedItem | undefined,
  qty: string,
  { expandKits = true }: { expandKits?: boolean } = {},
): CaptureBlock[] {
  if (!item || item.type === 'service') return [];
  const typed = qty.trim();
  if (!QTY.test(typed) || /^0+(\.0+)?$/.test(typed)) return [];

  if (expandKits && item.type === 'bundle' && item.autoDeduct) {
    return item.components
      .filter((component) => component.componentTracking !== 'none')
      .map((component) => {
        // The server multiplies base_qty by the kit quantity in SQL and
        // rounds to 3; this is the same arithmetic on decimal strings, so
        // the count on screen is the count the refusal would use.
        const units = multiplyQtyByFactor(component.baseQty, typed, 3);
        return {
          componentItemId: component.componentItemId,
          name: component.componentName,
          kind: component.componentTracking as TrackingKind,
          units,
          needed: component.componentTracking === 'serial' ? wholeUnits(units) : null,
        };
      });
  }

  if (item.trackingType === 'none') return [];
  return [
    {
      componentItemId: null,
      name: item.name,
      kind: item.trackingType,
      units: typed,
      needed: item.trackingType === 'serial' ? wholeUnits(typed) : null,
    },
  ];
}

function wholeUnits(qty: string): number | null {
  return WHOLE.test(qty) ? parseInt(qty, 10) : null;
}

/** How many identifiers this block is still short of. Zero means done. */
export function stillMissing(block: CaptureBlock, typed: string): number {
  if (block.kind === 'batch') return typed.trim() === '' ? 1 : 0;
  if (block.needed === null) return 1; // not whole units — never satisfiable
  const picked = new Set(parseSerials(typed));
  return Math.max(0, block.needed - picked.size);
}

/** The quantity a block covers, without NUMERIC's trailing-zero padding. */
export function blockUnits(block: CaptureBlock): string {
  return trimZeros(block.units);
}

export interface CaptureText {
  [key: string]: string;
}

/** Every block on a line that is still short — what the sub-table footers. */
export function outstandingBlocks(
  blocks: readonly CaptureBlock[],
  text: CaptureText,
): CaptureBlock[] {
  return blocks.filter((block) => stillMissing(block, text[blockKey(block)] ?? '') > 0);
}

export interface TrackingPayload {
  serialNos?: string[];
  batchNo?: string;
  components?: { componentItemId: string; serialNos?: string[]; batchNo?: string }[];
}

/**
 * The typed blocks as the wire wants them. Half-filled blocks are sent as
 * they stand rather than dropped: the server's refusal names the item and the
 * count, which is a better message than a screen inventing its own.
 */
export function trackingPayload(
  blocks: readonly CaptureBlock[],
  text: CaptureText,
): TrackingPayload {
  const payload: TrackingPayload = {};
  const components: NonNullable<TrackingPayload['components']> = [];

  for (const block of blocks) {
    const typed = (text[blockKey(block)] ?? '').trim();
    if (typed === '') continue;
    const picked = block.kind === 'serial' ? { serialNos: parseSerials(typed) } : { batchNo: typed };
    if (block.kind === 'serial' && picked.serialNos!.length === 0) continue;
    if (block.componentItemId === null) Object.assign(payload, picked);
    else components.push({ componentItemId: block.componentItemId, ...picked });
  }

  if (components.length > 0) payload.components = components;
  return payload;
}

/** A stored document read back into the editor's per-block text. */
export function textFromStored(
  stored: {
    serialNos?: readonly string[] | null;
    batchNo?: string | null;
    components?: readonly { componentItemId: string; serialNos: string[] | null; batchNo: string | null }[];
  },
): CaptureText {
  const text: CaptureText = {};
  const own = (stored.serialNos ?? []).join(', ') || (stored.batchNo ?? '');
  if (own !== '') text['·self'] = own;
  for (const component of stored.components ?? []) {
    const value = (component.serialNos ?? []).join(', ') || (component.batchNo ?? '');
    if (value !== '') text[component.componentItemId] = value;
  }
  return text;
}
